import { useMemo, useState } from 'react';
import { Button, Card, Space, Tag, Input, Row, Col, Statistic } from 'antd';
import { PlusOutlined, TeamOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../../../lib/api';
import { adminPath } from '../../../lib/adminPaths';
import { formatClientTypeLabel } from '../../../lib/clientType';
import { ResponsiveTable, type ResponsiveColumnType } from '../../../components/common/responsive';
import { CreatePartnerCompanyModal } from './CreatePartnerCompanyModal';

const { Search } = Input;

interface PartnerCompanyRow {
  id: string;
  name: string;
  status: string;
  clientType?: string;
  billingModel: string;
  billingContactEmail?: string | null;
  supportEmail?: string | null;
  createdAt?: Date | string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizePartnerCompanyRow = (value: unknown): PartnerCompanyRow | null => {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.name !== 'string') {
    return null;
  }

  return {
    id: value.id,
    name: value.name,
    status: typeof value.status === 'string' ? value.status : 'inactive',
    clientType: typeof value.clientType === 'string' ? value.clientType : undefined,
    billingModel: typeof value.billingModel === 'string' ? value.billingModel : '-',
    billingContactEmail:
      typeof value.billingContactEmail === 'string' ? value.billingContactEmail : null,
    supportEmail: typeof value.supportEmail === 'string' ? value.supportEmail : null,
    createdAt: typeof value.createdAt === 'string' ? value.createdAt : undefined,
  };
};

export function PartnerListPage() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const { data, isLoading } = trpc.partnerAdmin.listCompanies.useQuery({
    searchQuery: searchQuery || undefined,
    page: currentPage,
    limit: pageSize,
  });

  const companies = useMemo(
    () => (data?.companies ?? []).flatMap((company) => {
      const normalized = normalizePartnerCompanyRow(company);
      return normalized ? [normalized] : [];
    }),
    [data?.companies]
  );

  const columns: ResponsiveColumnType<PartnerCompanyRow>[] = [
    {
      title: '会社名',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <a onClick={() => navigate(adminPath(`partners/${record.id}`))} style={{ fontWeight: 600 }}>
            {text}
          </a>
          <div style={{ fontSize: 12, color: '#888' }}>{record.id}</div>
        </div>
      ),
      mobileRole: 'title',
      mobilePriority: 1,
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status}
        </Tag>
      ),
      mobileRole: 'tag',
      mobilePriority: 2,
    },
    {
      title: '対象クライアント',
      dataIndex: 'clientType',
      key: 'clientType',
      width: 140,
      render: (value) => formatClientTypeLabel(value),
      mobileRole: 'description',
      mobilePriority: 3,
    },
    {
      title: '課金モデル',
      dataIndex: 'billingModel',
      key: 'billingModel',
      width: 140,
      render: (value) => value ?? '-',
      mobileRole: 'description',
      mobilePriority: 4,
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Button type="link" onClick={() => navigate(adminPath(`partners/${record.id}`))}>
          詳細
        </Button>
      ),
      mobileRole: 'hidden',
    },
  ];

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ margin: 0 }}>パートナー管理</h1>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsCreateModalOpen(true)}>
            新規パートナー作成
          </Button>
        </div>

        <Row gutter={16}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="パートナー数"
                value={data?.total || 0}
                prefix={<TeamOutlined />}
              />
            </Card>
          </Col>
        </Row>

        <Card>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Row gutter={16}>
              <Col flex="auto">
                <Search
                  placeholder="会社名で検索"
                  allowClear
                  enterButton={<SearchOutlined />}
                  onSearch={setSearchQuery}
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>

            <ResponsiveTable
              dataSource={companies}
              columns={columns}
              loading={isLoading}
              rowKey="id"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: data?.total || 0,
                onChange: (page, size) => {
                  setCurrentPage(page);
                  setPageSize(size || 20);
                },
                showSizeChanger: true,
                showTotal: (total) => `全 ${total} 件`,
              }}
              mobileCardConfig={{
                titleField: 'name',
                onClick: (record) => navigate(adminPath(`partners/${record.id}`)),
              }}
            />
          </Space>
        </Card>
      </Space>

      <CreatePartnerCompanyModal
        open={isCreateModalOpen}
        onCancel={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          utils.partnerAdmin.listCompanies.invalidate();
        }}
      />
    </div>
  );
}
